import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import OwnerPageLayout from '../../components/layout/OwnerPageLayout';
import { getApiBaseUrl } from '../../apiBase';
import './notification-queue.css';

function adminApi() {
    return axios.create({
        baseURL: getApiBaseUrl(),
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
}

function StatCard({ label, value, color }) {
    return (
        <div className="nq-stat-card">
            <div className="nq-stat-label">{label}</div>
            <div className="nq-stat-value" style={{ color }}>{value}</div>
        </div>
    );
}

function formatDate(iso) {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
}

export default function NotificationQueuePage() {
    const { t } = useTranslation(['adminNotifications', 'common']);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [stats, setStats] = useState({ pending: 0, sent: 0, retrying: 0, failed: 0 });
    const [filter, setFilter] = useState({ status: '', channel: '' });
    const [page, setPage] = useState(1);
    const [msg, setMsg] = useState('');
    const [loading, setLoading] = useState(false);

    const pageSize = 50;

    const loadStats = useCallback(async () => {
        try {
            const res = await adminApi().get('/admin/notifications/queue/stats');
            const byStatus = res.data?.byStatus || [];
            const map = Object.fromEntries(byStatus.map((x) => [x.status, x.count]));
            setStats({
                pending: map.pending || 0,
                sent: map.sent || 0,
                retrying: map.retrying || 0,
                failed: map.failed || res.data?.failedCount || 0,
            });
        } catch (e) {
            if (e.response?.status === 403) {
                setMsg(t('adminNotifications:adminOnly'));
            }
        }
    }, []);

    const loadQueue = useCallback(async () => {
        setLoading(true);
        setMsg('');
        try {
            const params = { page, pageSize };
            if (filter.status) params.status = filter.status;
            if (filter.channel) params.channel = filter.channel;
            const res = await adminApi().get('/admin/notifications/queue', { params });
            setItems(res.data?.items || []);
            setTotal(res.data?.total || 0);
        } catch (e) {
            setMsg(e.response?.data?.message || t('common:error'));
        } finally {
            setLoading(false);
        }
    }, [filter, page, pageSize, t]);

    useEffect(() => {
        loadStats();
    }, [loadStats]);

    useEffect(() => {
        loadQueue();
    }, [loadQueue]);

    async function retry(id) {
        try {
            await adminApi().post(`/admin/notifications/queue/${id}/retry`);
            setMsg(t('common:done'));
            loadQueue();
            loadStats();
        } catch (e) {
            setMsg(e.response?.data?.message || t('common:error'));
        }
    }

    async function retryAllFailed() {
        try {
            const res = await adminApi().post('/admin/notifications/queue/retry-all-failed');
            setMsg(res.data?.message || t('common:done'));
            loadQueue();
            loadStats();
        } catch (e) {
            setMsg(e.response?.data?.message || t('common:error'));
        }
    }

    async function cancel(id) {
        if (!window.confirm(t('adminNotifications:cancelConfirm'))) return;
        try {
            await adminApi().delete(`/admin/notifications/queue/${id}`);
            loadQueue();
            loadStats();
        } catch (e) {
            setMsg(e.response?.data?.message || t('common:error'));
        }
    }

    async function testConnection(id) {
        try {
            const res = await adminApi().post(`/admin/notifications/queue/${id}/test-connection`);
            const ok = res.data?.success;
            setMsg(ok ? t('adminNotifications:testOk') : (res.data?.error || t('common:error')));
        } catch (e) {
            setMsg(e.response?.data?.message || t('common:error'));
        }
    }

    const statusLabel = useMemo(() => ({
        pending: t('adminNotifications:statuses.pending'),
        sent: t('adminNotifications:statuses.sent'),
        retrying: t('adminNotifications:statuses.retrying'),
        failed: t('adminNotifications:statuses.failed'),
        cancelled: t('adminNotifications:statuses.cancelled'),
    }), [t]);

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    return (
        <OwnerPageLayout title={t('adminNotifications:queueTitle')} maxWidth={1280}>
            <div className="nq-stats-row">
                <StatCard label={`⏳ ${t('adminNotifications:stats.pending')}`} value={stats.pending} color="#ea580c" />
                <StatCard label={`✅ ${t('adminNotifications:stats.sent')}`} value={stats.sent} color="#16a34a" />
                <StatCard label={`🔄 ${t('adminNotifications:stats.retrying')}`} value={stats.retrying} color="#2563eb" />
                <StatCard label={`❌ ${t('adminNotifications:stats.failed')}`} value={stats.failed} color="#dc2626" />
            </div>

            {stats.failed > 0 && (
                <div className="nq-alert-banner">
                    <span>⚠️ {t('adminNotifications:failedAlert', { count: stats.failed })}</span>
                    <button type="button" onClick={retryAllFailed}>{t('adminNotifications:retryAll')}</button>
                </div>
            )}

            {msg ? <p className="nq-msg">{msg}</p> : null}

            <div className="nq-filters">
                <select
                    value={filter.status}
                    onChange={(e) => { setPage(1); setFilter((f) => ({ ...f, status: e.target.value })); }}
                >
                    <option value="">{t('adminNotifications:allStatuses')}</option>
                    <option value="pending">⏳ {t('adminNotifications:statuses.pending')}</option>
                    <option value="sent">✅ {t('adminNotifications:statuses.sent')}</option>
                    <option value="retrying">🔄 {t('adminNotifications:statuses.retrying')}</option>
                    <option value="failed">❌ {t('adminNotifications:statuses.failed')}</option>
                    <option value="cancelled">{t('adminNotifications:statuses.cancelled')}</option>
                </select>
                <select
                    value={filter.channel}
                    onChange={(e) => { setPage(1); setFilter((f) => ({ ...f, channel: e.target.value })); }}
                >
                    <option value="">{t('adminNotifications:allChannels')}</option>
                    <option value="email">📧 {t('adminNotifications:channels.email')}</option>
                    <option value="sms">💬 {t('adminNotifications:channels.sms')}</option>
                </select>
                <button type="button" className="nq-refresh" onClick={() => { loadQueue(); loadStats(); }}>
                    ↻
                </button>
            </div>

            <div className="nq-table-wrap">
                <table className="nq-table">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>{t('adminNotifications:colChannel')}</th>
                            <th>{t('adminNotifications:colRecipient')}</th>
                            <th>{t('adminNotifications:colType')}</th>
                            <th>{t('adminNotifications:colSchool')}</th>
                            <th>{t('adminNotifications:colAttempts')}</th>
                            <th>{t('adminNotifications:colError')}</th>
                            <th>{t('adminNotifications:colStatus')}</th>
                            <th>{t('adminNotifications:colDate')}</th>
                            <th>{t('adminNotifications:colActions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && items.length === 0 ? (
                            <tr><td colSpan={10}>{t('common:loading')}</td></tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan={10}>{t('adminNotifications:empty')}</td></tr>
                        ) : items.map((item) => (
                            <tr key={item.id} className={item.status === 'failed' ? 'nq-row-error' : ''}>
                                <td>#{item.id}</td>
                                <td>
                                    {item.channel === 'email' ? '📧' : '💬'}
                                    {t(`adminNotifications:channels.${item.channel}`, item.channel)}
                                </td>
                                <td>
                                    <div>{item.recipientName}</div>
                                    <div className="nq-small">{item.recipientEmail || item.recipientPhone}</div>
                                </td>
                                <td>{item.type}</td>
                                <td>{item.schoolName || '—'}</td>
                                <td>{item.attemptCount}/{item.maxAttempts}</td>
                                <td className="nq-error-text">{item.lastError?.substring(0, 80) || '—'}</td>
                                <td>
                                    <span className={`nq-badge nq-badge-${item.status}`}>
                                        {statusLabel[item.status] || item.status}
                                    </span>
                                </td>
                                <td>{formatDate(item.createdAt)}</td>
                                <td className="nq-actions">
                                    {item.status === 'failed' && (
                                        <>
                                            <button type="button" onClick={() => retry(item.id)}>
                                                {t('adminNotifications:retry')}
                                            </button>
                                            <button type="button" onClick={() => testConnection(item.id)}>
                                                {t('adminNotifications:testConnection')}
                                            </button>
                                        </>
                                    )}
                                    {item.status !== 'sent' && (
                                        <button type="button" className="nq-btn-danger" onClick={() => cancel(item.id)}>
                                            ✕
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <div className="nq-pagination">
                    <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>←</button>
                    <span>{page} / {totalPages}</span>
                    <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>→</button>
                </div>
            )}
        </OwnerPageLayout>
    );
}
